import { useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Download, FileUp, Loader2 } from "lucide-react";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TEMPLATE = [
  ["Postcode", "Full address", "Manager notes", "Shutter", "Shutter details"],
  ["E13 9AA", "1 Example Road, London", "Knock first", "No", ""],
  ["E6 1AA", "2 Example Road, London", "", "Yes", "Roller shutter"],
];

const downloadTemplate = () => {
  const csv = TEMPLATE.map((r) => r.map((c) => `"${c.replace(/"/g, '""')}"`).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = "addresses-template.csv";
  a.click();
};

const PreviewRow = ({ a }) => (
  <li className={`flex items-start gap-2 rounded-lg border p-2.5 text-sm ${a.error ? "border-rose-200 bg-rose-50/60" : "border-slate-200 bg-white"}`} data-testid={`import-address-row-${a.row}`}>
    {a.error ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-600" /> : <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />}
    <div className="min-w-0">
      <p>{a.full_address || "—"} · <span className="font-data font-bold text-blue-800">{a.postcode || "—"}</span>{a.has_shutter ? " · Shutter" : ""}</p>
      {a.error && <p className="text-rose-700">Row {a.row}: {a.error}</p>}
    </div>
  </li>
);

export const ImportAddressesDialog = ({ open, onOpenChange, onImport }) => {
  const input = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const valid = preview?.addresses.filter((a) => !a.error) || [];

  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setPreview(null);
    setError(null);
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", f);
      setPreview({ name: f.name, ...(await api.post("/addresses/parse-file", body)).data });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const close = (o) => {
    if (busy) return;
    if (!o) {
      setPreview(null);
      setError(null);
    }
    onOpenChange(o);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="import-addresses-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Import addresses</DialogTitle>
          <DialogDescription>Upload a CSV or Excel file with one address per row. They'll be added to this run's list; nothing is saved until you save the run.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={downloadTemplate} data-testid="import-addresses-template-button"><Download className="mr-1 h-4 w-4" /> Download template</Button>
          <Button type="button" onClick={() => input.current?.click()} disabled={busy} className="bg-blue-700 hover:bg-blue-800" data-testid="import-addresses-choose-file-button">
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <FileUp className="mr-1 h-4 w-4" />}{preview ? "Choose another file" : "Choose file"}
          </Button>
          <input ref={input} type="file" accept=".csv,.xlsx" hidden onChange={pick} data-testid="import-addresses-file-input" />
        </div>
        {error && <p role="alert" className="rounded-md bg-rose-50 p-3 text-sm text-rose-700" data-testid="import-addresses-error">{error}</p>}
        {preview && (
          <div className="space-y-2" data-testid="import-addresses-preview">
            <p className="text-sm font-medium text-slate-800" data-testid="import-addresses-summary">
              {preview.name} · {preview.total_rows} rows · {valid.length} ready to add{preview.total_rows > valid.length ? ` · ${preview.total_rows - valid.length} with errors (skipped)` : ""}
            </p>
            <ul className="space-y-1.5">{preview.addresses.map((a) => <PreviewRow key={a.row} a={a} />)}</ul>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => close(false)} disabled={busy} data-testid="import-addresses-cancel-button">Cancel</Button>
          <Button onClick={() => { onImport(valid); close(false); }} disabled={!valid.length || busy} className="bg-blue-700 hover:bg-blue-800" data-testid="import-addresses-add-button">
            Add {valid.length} address{valid.length === 1 ? "" : "es"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
